import { ELinks } from '@sharedInterfaces/globalEnums';
import { type EIdentityProvider, stageContext } from '@src/globals';

const clientId = `client_id=${stageContext.cognitoClientId}`;
const responseType = 'response_type=token';
const scope = 'scope=email+openid+phone';
const baseUrl = stageContext.cognitoHostedURL;
const logoutHostname = stageContext.logoutHostname ? stageContext.logoutHostname : window.location.hostname;

/**
 * Escapes the given host name.
 *
 * @param {string} hostName - The host name to escape.
 * @returns {string} The escaped host name.
 */
function escapeHostName(hostName: string): string {
    if (hostName === 'localhost') {
        return encodeURIComponent('http://localhost:3000/');
    }
    return encodeURIComponent(`https://${hostName}/`);
}

/**
 * Generates a login URL.
 *
 * @param {string} hostName - The host name.
 * @returns {string} The login URL.
 */
export function createLoginUrl(hostName: string): string {
    return `${baseUrl}/oauth2/authorize?${clientId}&${responseType}&${scope}&redirect_uri=${escapeHostName(hostName)}${ELinks.LOGGEDIN}`;
}

/**
 * Generates a corporate login URL with identity provider.
 *
 * @param {string} hostName - The host name.
 * @param {EIdentityProvider} identityProvider - The identity provider.
 * @returns {string} The corporate login URL.
 */
export function createCorporateLoginUrl(hostName: string, identityProvider: EIdentityProvider): string {
    return `${baseUrl}/oauth2/authorize?identity_provider=${identityProvider}&redirect_uri=${escapeHostName(hostName)}${ELinks.LOGGEDIN}&${responseType}&${clientId}&scope=email openid phone`;
}

/**
 * Generates a forgot password URL.
 *
 * @param {string} hostName - The host name.
 * @returns {string} The forgot password URL.
 */
export function createForgotPasswordUrl(hostName: string): string {
    return `${baseUrl}/forgotPassword?${clientId}&${responseType}&${scope}&redirect_uri=${escapeHostName(hostName)}${ELinks.LOGGEDIN}`;
}

/**
 * Generates a sign up URL.
 *
 * @param {string} hostName - The host name.
 * @returns {string} The sign up URL.
 */
export function createSignUpUrl(hostName: string): string {
    return `${baseUrl}/signup?${clientId}&${responseType}&${scope}&redirect_uri=${escapeHostName(hostName)}${ELinks.LOGGEDIN}`;
}

/**
 * Generates a logout URL.
 * @returns {string} - The logout URL.
 */
export function createLogoutUrl(): string {
    return `${baseUrl}/logout?${clientId}&${responseType}&${scope}&logout_uri=${escapeHostName(logoutHostname)}${ELinks.LOGGEDOUT}`;
}
